<template>
  <div class="phone-input-wrapper">
    <div 
      class="phone-input-container" 
      :class="{ 
        'error': error || !isValid,
        'focus': showDropdown
      }"
    >
      <div class="country-selector" @click="toggleDropdown">
        <span v-if="selectedCountry" class="country-flag">{{ selectedCountry.emoji }}</span>
        <span v-else class="country-placeholder">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clip-rule="evenodd" />
          </svg>
        </span>
        <span class="dropdown-arrow" :class="{ 'rotated': showDropdown }">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </span>
      </div>
      <span v-if="dialCode" class="dial-code">{{ dialCode }}</span>
      <input
        ref="phoneInput"
        v-model="phoneNumber"
        type="tel"
        :placeholder="placeholder"
        class="phone-number-input"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
      />
    </div>
    <div v-if="showDropdown" class="country-dropdown" @click.stop>
      <div class="country-search-container">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Tìm quốc gia..."
          class="country-search"
          @input="filterCountries"
        />
      </div>
      <div class="country-list">
        <div
          v-for="country in filteredCountries"
          :key="country.id"
          class="country-item"
          :class="{ 'selected': selectedCountry && selectedCountry.id === country.id }"
          @click="selectCountry(country)"
        >
          <span class="country-emoji">{{ country.emoji }}</span>
          <span class="country-name">{{ country.name }}</span>
          <span class="country-dial">{{ country.dial_code }}</span>
        </div>
        <div v-if="filteredCountries.length === 0" class="no-results">
          Không tìm thấy quốc gia
        </div>
      </div>
    </div>
    <span
      v-if="(errorMessage && error) || (!isValid && phoneNumber)"
      class="text-xs text-red-400 leading-2 mt-1 block"
    >
      {{ errorMessage || $t('GENERAL.PHONE_INPUT.INVALID_PHONE') }}
    </span>
  </div>
</template>

<script>
import countries from 'shared/constants/countries.js';

export default {
  name: 'PhoneInput',
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Enter phone number',
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'blur', 'setCode', 'setDialCode', 'input'],
  data() {
    return {
      phoneNumber: '',
      selectedCountry: null,
      dialCode: '',
      showDropdown: false,
      searchQuery: '',
      countries: countries,
      isValid: true,
    };
  },
  computed: {
    filteredCountries() {
      if (!this.searchQuery) return this.countries;
      return this.countries.filter(country =>
        country.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        country.dial_code.includes(this.searchQuery)
      );
    },
  },
  watch: {
    modelValue(newValue) {
      this.phoneNumber = newValue || '';
      // Don't auto-parse phone numbers to prevent country code changes
    },
  },
  mounted() {
    this.phoneNumber = this.modelValue || '';
    this.parsePhoneNumber();
    
    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    onInput() {
      this.validatePhoneNumber();
      this.$emit('update:modelValue', this.phoneNumber);
      this.$emit('input', this.phoneNumber);
    },
    validatePhoneNumber() {
      // Simple regex for phone numbers: only + and digits
      const phoneRegex = /^[\+]?[\d]{9,12}$/;
      this.isValid = phoneRegex.test(this.phoneNumber) || this.phoneNumber === '';
    },
    onBlur() {
      // Delay to allow click on dropdown items
      setTimeout(() => {
        this.showDropdown = false;
      }, 150);
      this.$emit('blur');
    },
    onFocus() {
      // Optional: Auto-open dropdown on focus
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.positionDropdown();
          const searchInput = this.$el.querySelector('.country-search');
          if (searchInput) searchInput.focus();
        });
      }
    },
    positionDropdown() {
      const container = this.$el.querySelector('.phone-input-container');
      const dropdown = this.$el.querySelector('.country-dropdown');
      
      if (container && dropdown) {
        const rect = container.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = 250; // max-height
        
        // Position dropdown below the input
        let top = rect.bottom + 4;
        let left = rect.left;
        
        // Check if dropdown would go off screen
        if (top + dropdownHeight > viewportHeight) {
          // Position above the input instead
          top = rect.top - dropdownHeight - 4;
        }
        
        // Check if dropdown would go off screen horizontally
        if (left + 300 > window.innerWidth) {
          left = window.innerWidth - 300 - 10;
        }
        
        dropdown.style.top = `${top}px`;
        dropdown.style.left = `${left}px`;
        dropdown.style.width = `${Math.max(300, rect.width)}px`;
      }
    },
    selectCountry(country) {
      this.selectedCountry = country;
      this.dialCode = country.dial_code;
      this.showDropdown = false;
      this.searchQuery = '';
      this.$emit('setCode', country.id);
      this.$emit('setDialCode', country.dial_code);
    },
    filterCountries() {
      // Computed property handles this
    },
    parsePhoneNumber() {
      // Only parse when the phone number is set from outside (modelValue change)
      // Don't parse during user typing to avoid automatic country code changes
      if (this.phoneNumber && this.phoneNumber.startsWith('+')) {
        const dialCode = this.phoneNumber.match(/^\+\d{1,4}/);
        if (dialCode) {
          this.dialCode = dialCode[0];
          const country = this.countries.find(c => c.dial_code === this.dialCode);
          if (country) {
            this.selectedCountry = country;
          }
        }
      }
    },
    handleClickOutside(event) {
      if (this.showDropdown && !this.$el.contains(event.target)) {
        this.showDropdown = false;
      }
    },
  },
};
</script>

<style scoped>
.phone-input-wrapper {
  position: relative;
  width: 100%;
  z-index: 1;
}

.phone-input-container {
  display: flex;
  align-items: center;
  border: 1px solid var(--s-75);
  border-radius: 0.5rem;
  background: var(--white);
  overflow: hidden;
  transition: all 0.2s ease;
  min-height: 2.5rem;
}

.phone-input-container:hover {
  border-color: var(--s-200);
}

.phone-input-container.focus {
  border-color: var(--w-500);
  box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
}

.phone-input-container.error {
  border-color: var(--r-500);
  box-shadow: 0 0 0 3px rgba(255, 56, 45, 0.1);
}

.country-selector {
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  background: var(--s-50);
  border-right: 1px solid var(--s-75);
  cursor: pointer;
  min-width: 4.375rem;
  transition: background-color 0.2s ease;
}

.country-selector:hover {
  background: var(--s-100);
}

.country-flag {
  margin-right: 0.375rem;
  font-size: 1rem;
}

.country-placeholder {
  margin-right: 0.375rem;
  color: var(--s-500);
  display: flex;
  align-items: center;
}

.dropdown-arrow {
  color: var(--s-500);
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dial-code {
  padding: 0.625rem 0.5rem;
  background: var(--white);
  color: var(--s-700);
  font-weight: 500;
  font-size: 0.875rem;
  border-right: 1px solid var(--s-75);
}

.phone-number-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  color: var(--color-body);
  background: transparent;
}

.phone-number-input::placeholder {
  color: var(--s-400);
}

.country-dropdown {
  position: fixed;
  top: auto;
  left: auto;
  right: auto;
  background: var(--white);
  border: 1px solid var(--s-75);
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  max-height: 15.625rem;
  overflow: hidden;
  animation: slideDown 0.2s ease;
  min-width: 18.75rem;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-0.625rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.country-search-container {
  padding: 0.5rem;
  border-bottom: 1px solid var(--s-75);
}

.country-search {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--s-75);
  border-radius: 0.375rem;
  outline: none;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  background: var(--white);
  color: var(--color-body);
}

.country-search:focus {
  border-color: var(--w-500);
  box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
}

.country-list {
  max-height: 11.25rem;
  overflow-y: auto;
}

.country-item {
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--s-50);
}

.country-item:last-child {
  border-bottom: none;
}

.country-item:hover {
  background: var(--s-50);
}

.country-item.selected {
  background: var(--w-50);
  color: var(--w-700);
}

.country-emoji {
  margin-right: 0.625rem;
  font-size: 1rem;
}

.country-name {
  flex: 1;
  font-size: 0.875rem;
  color: var(--s-700);
  font-weight: 500;
}

.country-dial {
  font-size: 0.8125rem;
  color: var(--s-500);
  font-weight: 600;
  background: var(--s-100);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.no-results {
  padding: 1.25rem;
  text-align: center;
  color: var(--s-500);
  font-size: 0.875rem;
  font-style: italic;
}

/* Dark mode support */
.dark .phone-input-container {
  background: var(--s-800);
  border-color: var(--s-600);
}

.dark .phone-input-container:hover {
  border-color: var(--s-500);
}

.dark .phone-input-container.focus {
  border-color: var(--w-500);
}

.dark .country-selector {
  background: var(--s-700);
  border-color: var(--s-600);
}

.dark .country-selector:hover {
  background: var(--s-600);
}

.dark .dial-code {
  background: var(--s-800);
  color: var(--s-100);
  border-color: var(--s-600);
}

.dark .phone-number-input {
  color: var(--s-100);
}

.dark .phone-number-input::placeholder {
  color: var(--s-400);
}

.dark .country-dropdown {
  background: var(--s-800);
  border-color: var(--s-600);
}

.dark .country-search {
  background: var(--s-700);
  border-color: var(--s-600);
  color: var(--s-100);
}

.dark .country-item:hover {
  background: var(--s-700);
}

.dark .country-item.selected {
  background: var(--w-700);
  color: var(--w-50);
}

.dark .country-name {
  color: var(--s-100);
}

.dark .country-dial {
  background: var(--s-600);
  color: var(--s-200);
}
</style>
